<template>
  <v-container>
    <v-row>
      <project-summary v-bind:isBalance="false"></project-summary>
    </v-row>
    <v-row class="plant-production__acreage">
      <span>
        <v-text-field ref="acreageInputField"
          v-model="project.acreage"
          :prefix="$t('plantProduction.acreage')"
          suffix="ha"
          @keypress="preventUndesiredInput"
          v-on:input="setProjectAcreage(project.acreage)"
        ></v-text-field>
      </span>
      <span>
        <v-text-field
          :prefix="$t('plantProduction.usedAcreage')"
          suffix="ha"
          :value="balance.acreage_sum"
          readonly
          type="number"
        >
          <template v-slot:append-outer>
            <v-tooltip right v-if="balance.acreage_sum > project.acreage">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  >
                    mdi-alert
                  </v-icon>
              </template>
              <span>{{ $t('plantProduction.messages.acreageExceeded') }}</span>
            </v-tooltip>
          </template>
        </v-text-field>
      </span>
    </v-row>
    <v-row>
      <v-text-field
        class="search-text"
        v-model="search"
        hide-details
        :label="$t('search.text')"
        :append-icon="this.search.length == 0 ? 'mdi-magnify' : 'mdi-close'"
        @click:append="search = ''"
      />
    </v-row>
    <v-row class="project-content">
      <v-col cols="10">
        <v-card flat>
          <v-data-table
            class="data-table__project"
            id="dataTable"
            ref="dataTable"
            :headers="headers"
            fixed-header
            height="70vh"
            :items="this.project.plant_output"
            :sort-by="this.nameProperty"
            default-sort="false"
            :options.sync="options"
            @click:row="itemDetails"
            hide-default-footer
            :item-key="`name_${this.$store.locale}`"
            group-by="category_id"
            :single-expand="false"
            single-select
            :search="search"
          >
            <template v-slot:group.header="{ group, items, isOpen, toggle }">
              <td>
                <v-btn
                  @click="toggle"
                  icon
                  :ref="group"
                  v-ripple="false"
                  class="data-table__toggle-btn"
                >
                  <v-icon v-if="isOpen">
                    mdi-chevron-down
                  </v-icon>
                  <v-icon v-else>
                    mdi-chevron-right
                  </v-icon>
                </v-btn>
              </td>
              <td class="text-left">
                {{ getCategoryName(group) }}
              </td>
              <td>
                {{ getAcreageSum(items) }}
              </td>
              <td></td>
              <td></td>
              <td></td>
              <td>
                <v-tooltip top 
                  open-delay="800">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon
                      v-bind="attrs"
                      v-on="on"
                      >
                      <v-icon 
                        medium
                        class="data-table__add-output-btn" 
                        @click="addNewPlantOutputToggle({ category_id: group }, toggle, isOpen)"
                        >
                        mdi-plus
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t('plantProduction.dataTable.tooltipAddToCategory') }}</span>
                </v-tooltip>
              </td>
            </template>

            <template v-slot:item="val">
              <v-lazy :value="val.isExpanded" 
              tag="tr"
              transition=""
              >
                <td></td>
                <td class="text-left">
                  <v-tooltip v-if="hasDescription(val.item)"
                    bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        :value="val.item['name_'+$i18n.locale] ? val.item['name_'+$i18n.locale] : getItemName(val.item)"
                        :readonly="val.item.id != undefined"
                        @blur="setOutputName(val.item, $event.target.value)"
                        @keyup="setOutputName(val.item, $event.target.value)"
                        hide-details
                        v-bind="attrs"
                        v-on="on"
                        @click="itemDetails(val.item)"
                      />
                    </template>
                    <span>{{val.item['description_'+$i18n.locale]}}</span>
                  </v-tooltip>
                  <v-text-field v-else
                    :value="val.item['name_'+$i18n.locale] ? val.item['name_'+$i18n.locale] : getItemName(val.item)"
                    :autofocus="focusOnEditButton(val.item)"
                    :key="val.item.added_uid"
                    @blur="setOutputName(val.item, $event.target.value)"
                    @keyup="setOutputName(val.item, $event.target.value)"
                    :readonly="val.item.id != undefined"
                    hide-details
                    @click="itemDetails(val.item)"
                  />
                </td>
                <td class="text-right" >
                  <v-text-field
                    :key="acreage_input_key"
                    v-model.number="val.item.acreage_input"
                    @keypress="preventUndesiredInput"
                    @keyup="handlerAcreage(val.item.acreage_input, val.item)"
                    v-on:input="setAcreage(val.item, $event)"
                    size="6"
                    hide-details
                    type="number"
                    @wheel="$event.currentTarget.blur()"
                    :append-icon="val.item.multiple_use ? 'mdi-calculator' : ''"
                    @click:append="val.item.included = !val.item.included"
                    @click="itemDetails(val.item)"
                    />
                </td>
                <td class="text-right">
                  <input
                    class="input-number" 
                    :key="acreage_pct_key" 
                    v-model="val.item.acreage_input_pct"
                    v-on:input="setAcreagePct(val.item, $event.target.value)"
                    :class="{ calc: val.item.included }"
                    hide-details
                    @click="itemDetails(val.item)"
                  />
                </td>
                <td class="text-right">
                  <v-text-field
                    hide-details
                    type="number"
                    :readonly="val.item.id != undefined"
                    button__secondary
                    v-model="val.item.yield_actual"
                    @click="itemDetails(val.item)"
                  ></v-text-field>
                </td>
                <td class="text-right">
                  <span @click="itemDetails(val.item)">{{ getAcreageTotal(val.item) }}</span>
                </td>
                <td>
                  <v-menu bottom left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon v-bind="attrs" v-on="on">
                        <v-icon medium color="grey">
                          mdi-dots-horizontal
                        </v-icon>
                      </v-btn>
                    </template>
                    <v-list v-if="!val.item.id">
                      <v-list-item
                        v-for="(elem, i) in context_menu"
                        :key="i"
                        @click="elem.action(val.item)"
                      >
                        <v-list-item-title>{{ elem.title }}</v-list-item-title>
                        </v-list-item>
                        </v-list>
                        <v-list v-else>
                        <v-list-item @click="addNewPlantOutput(val.item)">
                          <v-list-item-title>{{ $t('contextMenu.duplicate') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </v-lazy>
            </template>
          </v-data-table>
        </v-card>
      </v-col>

      <v-col cols="2" sm="6" md="2">
        <v-card class="data-table__details-view plant-details" tile>
          <td v-if="selectedId !== -1">
            <span class="data-table__details-view-headline">{{ selected_output[`name_${$i18n.locale}`] }}</span>
            <p></p>
            <p font-size="small" v-if="selected_output">
              <span class="data-table__details-view-sub-headline">{{ $t('plantProduction.detailsView.demand') }}</span>
            </p>
            <p v-if="selected_output.multiple_use">
                <v-checkbox
                  v-model="selected_output.included"
                  :checked="selected_output.included"
                  v-on:input="selected_output.included = $event.target.value"
                  :label="$t('plantProduction.detailsView.calcText')"
                >
              </v-checkbox>
            </p>
            <p></p>
            <p>
             <v-text-field
                :readonly="selected_output.id != undefined"
                v-model="selected_output.N"
                prefix="N:"
                suffix="kg/ha"
                @keypress="preventUndesiredInput"
                v-on:input="setN(selected_output, $event)"
                type="number"
              ></v-text-field>
          </p>
          <p>
            <v-text-field
                :readonly="selected_output.id != undefined"
                v-model="selected_output.P"
                prefix="P:"
                suffix="kg/ha"
                @keypress="preventUndesiredInput"
                v-on:input="setP(selected_output, $event)"
                type="number"
              ></v-text-field>
          </p>
          <p>
            <v-text-field
                :readonly="selected_output.id != undefined"
                v-model="selected_output.K"
                prefix="K:"
                suffix="kg/ha"
                @keypress="preventUndesiredInput"
                v-on:input="setK(selected_output, $event)"
                type="number"
              ></v-text-field>
            </p>
            <p v-if="this.selected_output.bnf">
              <v-text-field
                :value="selected_output.bnf"
                :readonly="selected_output.id != undefined"
                prefix="BNF:"
                suffix="kg/ha"
                type="number"
              ></v-text-field>
            </p>
            <p v-if="this.selected_output.category_id.includes('crop')">
              {{ $t('plantProduction.detailsView.standardYield') }} {{ selected_output.main_product }}
              {{ selected_output.yield }} {{ $t('plantProduction.detailsView.suffix') }}
            </p>
          </td>
          <td v-else>
           <span class="data-table__details-view-headline">{{ $t('plantProduction.detailsView.title') }}</span>
            <p></p>
            <p>
              <span class="data-table__details-view-sub-headline">
              {{ $t('plantProduction.detailsView.subtext') }}
              </span>
            </p>
            <p></p>
          </td>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import store from "@/store/index";
import ProjectSummary from '../components/ProjectSummary.vue';
import { nameCheck } from "@/main.js";


export default {
  components: { ProjectSummary },
  data() {
    return {
      context_menu: [
        {
          title: this.$i18n.t('contextMenu.remove'),
          action: function(item) {
            store.dispatch("removePlantOutput", item);
          },
        },
        {
          title: this.$i18n.t('contextMenu.duplicate'),
          action: function(item) {
            store.commit("addNewPlantOutput", item);
          },
        },
      ],

      editedItem: " ",
      items: [{ title: "Duplizieren" }],
      options: {
        itemsPerPage: -1,
      },
      headers: [
        {
          text: " ",
          sortable: false,
          width: 50
        },
        {
          text: this.$i18n.t('plantProduction.dataTable.name'),
          sortable: false,
          value: "name_"+this.$i18n.locale,
          align: "left",
          width: 450
        },
        { text: this.$i18n.t('plantProduction.dataTable.acreage'), value: "acreage_input", align: "right", width: 150 },
        { text: this.$i18n.t('plantProduction.dataTable.acreagePct'), value: "acreage_input_pct", align: "right", width: 150 },
        { text: this.$i18n.t('plantProduction.dataTable.yieldTHaY'), value: "yield_actual", align: "right", width: 150 },
        { text: this.$i18n.t('plantProduction.dataTable.yieldTY'), value: "total_yield", align: "right", width: 150 },
        { text: " ", value: "actions", sortable: false, align: "right", width: 50 },
      ],
      selected_output: {},
      newName: "",
      selectedId: -1,
      acreage_input_key: 0,
      acreage_pct_key: 0,
      search: ''
    };
  },

  computed: {
    ...mapGetters(['balance']),
    ...mapState(['project','categories','locale']),
    usedAcreage: {
      get() {
        return this.balance.items
        .filter(output => output.acreage_input)
        .reduce((val, output) => parseInt(val) + parseInt(output.acreage_input), 0);
      }
    },
    nameProperty: function () {
      return 'name_'+this.locale
    },

  },

  mounted() {
    // on entering an existing project
    this.collapseTableGroups(this, "dataTable");
  },

  methods: {
    hasDescription(item) {
      if (item['description_'+this.$i18n.locale] && item['description_'+this.$i18n.locale].length > 0) {
        return true;
      } else {
        return false;
      }
    },
    preventUndesiredInput($event) {
      var keyCode = ($event.keyCode ? $event.keyCode : $event.which);
      if (keyCode < 48 || keyCode > 57) {
        $event.preventDefault();
      }
    },

    getItemName(item) {
      if (!item['name_'+this.$i18n.locale]) {
        return "";
      }
    },

    setOutputName(item, newValue) {
      if (newValue === "") {
        let names = this.project.plant_output.map(out => out['name_'+this.$i18n.locale])
        let newName = this.getCategoryName(item.category_id)
        newName = nameCheck(newName, names)
        item.name_de = newName;
        item.name_en = newName
      } else {
        item.name_de = newValue;
        item.name_en = newValue
      }
    },

    setPlantInputName(val, item) {
      if (val) {
        item['name_'+this.$i18n.locale] = val;
      }
    },

    handlerActualYield(val, item) {
      if (val === "0") {
        item.yield_actual = item.yield;
      }
    },

    handlerAcreage(val, item) {
      if (val === "") {
        item.acreage_input = 0;
      }
    },

    getCategoryName(category) {
      let cat = this.categories.find((c) => c.id == category);
      return cat ? cat['name_'+this.$i18n.locale] : "";
    },

    focusOnEditButton(item) {
      if (item.added_uid && item['name_'+this.$i18n.locale] === '') {
        return true
      } else {
        return false
      }
    },

    addNewPlantOutputToggle(item, toggle, isOpen) {
      if(!isOpen){toggle()}
      this.$store.commit("addNewPlantOutput", item);
    },

    addNewPlantOutput(item) {
      console.log("Duplicate item",item);
      this.$store.commit("addNewPlantOutput", item);
    },

    setAcreage(item,newValue) {
      if (newValue) {
        item.acreage_input = parseFloat(newValue);
        this.setPercentValue(item);
        this.acreage_pct_key += 1;
      } else {
        item.acreage_input = null;
        item.acreage_input_pct = null;
      }
      store.commit('updateDemandFulfillments');
    },

    setPercentValue(item) {
      if (this.project.acreage) { 
        item.acreage_input_pct = ((item.acreage_input * 100)/this.project.acreage).toFixed(1);
      }
    },

    getAcreagePct(item) {
      if (item.acreage_input && this.project.acreage) {
        return ((item.acreage_input / this.project.acreage) * 100).toFixed(1);
      } else {
        return "";
      }
    },

    updateAcreagePct() {
      if (this.project.acreage) {
        for(var key in this.project.plant_output) {
          var plant = this.project.plant_output[key];
          if (plant.acreage_input) {
            let pct = (plant.acreage_input / this.project.acreage * 100).toFixed(2);
            plant.acreage_input_pct = pct;
          }
        }        
      }
      else {
        for(var elem in this.project.plant_output) {
          var pl = this.project.plant_output[elem];
          if (pl.acreage_input) {
            pl.acreage_input_pct = null;
          }
        }
      }
    },

    setAcreagePct(item, val) {
      if (val && Number(val)) {
        item.acreage_input = parseInt((this.project.acreage * val)/100);
        item.acreage_input_pct=item.acreage_input_pct.match(/^\d+\.?\d{0,2}/);
      }
      else if (!val) {
        item.acreage_input = null;
      }
      this.acreage_input_key += 1;
      store.commit('updateDemandFulfillments');
    },

    setN(item, val) {
      if (val && Number(val)) {
        item.N = val;
      }
    },

    setP(item, val) {
      if (val && Number(val)) {
        item.P = val;
      }
    },

    setK(item, val) {
      if (val && Number(val)) {
        item.K = val;
      }
    },

    getAcreageTotal(item) {
      let result = undefined;
      if (item.yield_actual && item.acreage_input) {
        result = (item.yield_actual * item.acreage_input).toFixed(0);
      }
      if (Number.isNaN(result)) {
        result = "";
      }
      return result;
    },

    getAcreageSum(items) {
      return items
        .filter(item => item.acreage_input && item.included)
        .reduce((val, item) => val + item.acreage_input, 0);
    },

    setProjectAcreage(val) {
      if (val) {
        this.project.acreage = parseFloat(val);
      }
      this.updateAcreagePct();
    },

    hasOutputForNutrientValue(proj, nutrientValue) {
      return proj.plant_output.findIndex((p) => p.id == nutrientValue.id) >= 0;
    },

    duplicateItem(item) {
      this.editedItem = Object.assign({}, item);
      this.$router.push("/projects/" + this.editedItem.project_id);
    },

    itemDetails(item) {
      this.selected_output = item;
      this.selectedId = item['name_'+this.$i18n.locale];
    },

    /**
     * For a v-data-table using grouping this function collapses
     * all groups except for the first one.
     * @param vue a VueComponent instance (here just 'this')
     * @param ref the 'ref' value of a v-data-table
     */
    collapseTableGroups(vue, ref, expanded=false) {
      // lookup the table instance
      let table = vue.$refs[ref];
      // the $refs array contains the group labels and the table's ref name
      let refs = Object.keys(vue.$refs);
      // by setting the value on the table's 'openCache' to true/false, the groups
      // are either expanded or collapsed
      // must use $set to have this reactive
      refs.forEach(function(element) {
        table.$set(table.openCache, element, expanded);
      });
    },
  },

  watch: {
    project() {
      // when a new project is created
      this.$nextTick(() => {
        this.collapseTableGroups(this, "dataTable");
      });
    },
    search() {
      this.collapseTableGroups(this, "dataTable", this.search.length > 0);
    }
  }
};
</script>
